/**
 * SkipLink Component
 * Provides keyboard navigation skip links for better accessibility
 * Allows users to skip to main content, forms, or other key sections
 */

import { type ReactNode } from 'react';
import { cn } from '../../lib/utils';

export interface SkipLinkProps {
  href: string;
  children: ReactNode;
  className?: string;
}

export function SkipLink({ href, children, className }: SkipLinkProps) {
  return (
    <a
      href={href}
      className={cn(
        // Visually hidden by default, visible on focus
        'sr-only focus:not-sr-only',
        // Positioning when focused
        'focus:absolute focus:top-4 focus:left-4 focus:z-50',
        // Styling when focused
        'focus:bg-sky-600 focus:text-white focus:px-4 focus:py-2 focus:rounded-md focus:shadow-lg',
        // Focus indicators
        'focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-sky-600',
        // High contrast support
        'high-contrast:focus:bg-black high-contrast:focus:text-white high-contrast:focus:ring-white',
        // Animation
        'transition-all duration-200',
        className
      )}
      tabIndex={0}
    >
      {children}
    </a>
  );
}

/**
 * SkipLinks Container
 * Provides a set of common skip links for authentication pages
 */
export interface SkipLinksProps {
  showMainContent?: boolean;
  showForm?: boolean;
  showHelpLinks?: boolean;
  className?: string;
}

export function SkipLinks({
  showMainContent = true,
  showForm = true,
  showHelpLinks = false,
  className,
}: SkipLinksProps) {
  return (
    <div className={cn('sr-only focus-within:not-sr-only', className)}>
      {showMainContent && (
        <SkipLink href="#main-content">Skip to main content</SkipLink>
      )}
      {showForm && <SkipLink href="#main-form">Skip to form</SkipLink>}
      {showHelpLinks && (
        <SkipLink href="#help-links">Skip to help links</SkipLink>
      )}
    </div>
  );
}
